import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Marquee from "react-fast-marquee";
import DataTable, { TableColumn } from "react-data-table-component";
import BoxComponent from "../../components/CommonComponents/BoxComponent/BoxComponent";
import { tableCustomStyles } from "../../components/CommonComponents/Table/Table";
import { _axios } from "../../utils/axios";
import { BASE_URL } from "../../constants/Constants";
import { APP_CONFIG } from "../../config/appConfig";
import "./QRFiles.css";

import QRApplicationInfoService, {
    QRApplicationInfoResponse,
    QRTimelineRow,
} from "./services/qrApplicationInfo.service";

import style from "../landingPage/landing.module.css";

/* ---------------- HEADER ---------------- */

function LandingHeader() {
    const [currentDateTime, setCurrentDateTime] = useState<string>(() => {
        const istOptions: Intl.DateTimeFormatOptions = {
            timeZone: "Asia/Kolkata",
            weekday: "long",
            month: "long",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "numeric",
        };
        return new Date().toLocaleString("en-IN", istOptions);
    });

    const [tickerData, setTickerData] = useState<any[]>([]);
    const [logoUrl, setLogoUrl] = useState<string>("");



    useEffect(() => {
        _axios.get(`${BASE_URL}/api/logo`).then((res) => {
            if (res.data?.success === 1) setLogoUrl(res.data.url);
        });

        const fetchTicker = async () => {
            const date = new Date().toLocaleDateString("en-GB").split("/").join("-");
            const res = await fetch(`${BASE_URL}/api/ticker/status`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ current_date: date }),
            });
            const data = await res.json();
            setTickerData(data.data || []);
        };

        fetchTicker();

        const interval = setInterval(() => {
            setCurrentDateTime(new Date().toLocaleString("en-IN", {
                timeZone: "Asia/Kolkata",
                weekday: "long",
                month: "long",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "numeric",
            }));
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    return (<>
        <header className={style.header}>
            {logoUrl && <img src={logoUrl} alt="Logo" className={style.logo} />}
            <div className={style.loginInfo}>
                <div className={style.dateTimeWeather}>
                    <div className={style.dateWrapper}>
                        <div>{currentDateTime.split(",")[0]},</div>
                        <div>{currentDateTime.split(",").slice(1).join(",")}</div>
                    </div>
                </div>
            </div>
        </header>

        <Marquee className={style.marqueeLine} gradient={false} speed={70}>
            {tickerData.map((t, i) => (<span key={i} style={{ marginRight: 20, color: "#fff" }}>
                {t.text_details}
            </span>))}
        </Marquee>
    </>);
}

/* ---------------- MAIN QR PAGE ---------------- */

const QRFiles = () => {
    const { applicationId } = useParams<{ applicationId: string }>();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<QRApplicationInfoResponse | null>(null);
    const [timeline, setTimeline] = useState<QRTimelineRow[]>([]);
    const [error, setError] = useState<string | null>(null);

    const qrTableStyles = {
        ...tableCustomStyles,
        headRow: {
            style: {
                ...tableCustomStyles.headRow?.style,
                minHeight: "80px",          // ✅ THIS increases header row height
                alignItems: "center",
            },
        },
        headCells: {
            style: {
                ...tableCustomStyles.headCells?.style,
                paddingTop: "12px",
                paddingBottom: "12px",
                whiteSpace: "normal",
                lineHeight: "1.4",
                fontSize: "14px",
            },
        },
        cells: {
            style: {
                whiteSpace: "normal",     // ✅ allow wrapping
                lineHeight: "1.4",
                paddingTop: "10px",
                paddingBottom: "10px",
                fontSize: "13px",
            },
        },
    };

    const formatDate = (value?: string) => {
        if (!value) return "NA";
        const d = new Date(value);
        return d.toLocaleDateString("en-GB"); // DD/MM/YYYY
    };

    useEffect(() => {
        if (!applicationId) {
            setError("Invalid Application Id");
            setLoading(false);
            return;
        }

        Promise.all([QRApplicationInfoService.getApplicationInfo(applicationId), QRApplicationInfoService.getApplicationTimeline(applicationId),])
            .then(([info, timeline]) => {
                setData(info);
                setTimeline(timeline);
            })
            .catch(() => setError("Failed to load QR details"))
            .finally(() => setLoading(false));
    }, [applicationId]);

    if (loading) return <div className="qr-loading">Loading...</div>;
    if (error) return <div className="qr-error">{error}</div>;
    if (!data) return null;

    const timelineColumns: TableColumn<QRTimelineRow>[] = [
        {
            name: "Sr No",
            selector: (row) => row.stepNo,
            center: true,
            width: "80px",
        },
        {
            name: "Authority / Applicant Name",
            // selector: (row) => row.authorityOrApplicant,
            center: true,
            //width: "180px",
            cell: (row) => (
                <span className="text-center">{row.authorityOrApplicant}</span>
            ),
        },
        {
            name: "Designation",
            selector: (row) => row.designation,
            center: true,
            wrap: true,
            width: "150px",

        },
        {
            name: "Application Received Date",
            cell: (row) => formatDate(row.applicationReceivedDate),
            center: true,
            width: "240px",
            wrap: true,
        },
        {
            name: "Application Forwarded / Approved Date",
            //selector: (row) => formatDate(row.applicationForwardedOrApprovedDate),
            cell: (row) => formatDate(row.applicationForwardedOrApprovedDate),
            center: true,
            width: "240px",
            wrap: true,
        },
        {
            name: "Application Returned Date",
            selector: (row) => row.applicationReturnedDate || "",
            center: true,
            width: "240px",
            wrap: true,
            cell: (row) => (
                <span className={row.applicationReturnedDate ? "qr-status-success" : "qr-status-muted"}>
                    {row.applicationReturnedDate ? "✔" : ""}
                </span>
            ),
        },
        {
            name: "Documents Verified",
            selector: (row) => row.documentsVerified,
            center: true,
            width: "150px",
            cell: (row) => (
                <span className={row.documentsVerified === "✓" ? "qr-status-success" : "qr-status-muted"}>
                    {row.documentsVerified}
                </span>
            ),
        },
        {
            name: "Technical Approval",
            selector: (row) => row.technicalApproval,
            center: true,
            width: "150px",
            cell: (row) => (
                <span className={row.technicalApproval === "✓" ? "qr-status-success" : "qr-status-muted"}>
                    {row.technicalApproval}
                </span>
            ),
        },
        {
            name: "Application Status",
            //selector: (row) => row.applicationStatus,
            center: true,
            width: "150px",
            //cell: (d) => <span className="text-center">{d.oldValue || "-"}</span>,
            cell: (row) => (
                <span className="text-center">{row.applicationStatus}</span>
            ),
        },
    ];

    return (<>
        {/* UPPER LANDING SECTION */}
        <LandingHeader />

        {/* APPLICATION DETAILS */}
        <div className="qr-page">
            <div className="qr-container">

                {/* Page Title with Yellow Underline */}
                <div className="qr-title-wrapper">
                    <h2 className="qr-title">Application Details</h2>
                    <div className="qr-title-underline" />
                </div>


                {/* BOX 1: Application Status */}

                <div className="qr-box-spacing">

                    <div className="qr-box-header-flat">
                        Application status for gunthewari permission
                    </div>


                    <div className="qr-details-grid">

                        <div>
                            <span style={{ fontWeight: "600" }}>Application Number :</span>{" "}
                            <span>{data.applicationId}</span>
                        </div>
                        <div>
                            <span style={{ fontWeight: "600" }}>Plot Owner :</span>{" "}
                            <span>{data.plotOwner}</span>
                        </div>
                        <div>
                            <span style={{ fontWeight: "600" }}>Application Submission Date :</span>{" "}
                            <span>
                                {data.applicationSubmissionDate
                                    ? new Date(data.applicationSubmissionDate).toLocaleDateString()
                                    : "--"
                                }
                            </span>
                        </div>
                        <div>
                            <span style={{ fontWeight: "600" }}>Certificate Approval Date :</span>{" "}
                            <span>
                                {
                                    data.certificateApprovalDate
                                        ? new Date(data.certificateApprovalDate).toLocaleDateString()
                                        : "--"
                                }
                            </span>
                        </div>
                    </div>

                </div>

                {/* BOX 2: Site Details */}
                <div className="qr-box-spacing">

                    <div className="qr-box-header-flat">
                        Site Details
                    </div>


                    <div className="qr-details-grid">
                        <div>
                            <span style={{ fontWeight: "600" }}>Site Address :</span>{" "}
                            <span>{data.siteDetails.siteAddress}</span>
                        </div>
                        <div>
                            <span style={{ fontWeight: "600" }}>Plot Number:</span>{" "}
                            <span>{data.siteDetails.plotNo}</span>
                        </div>

                        <div>
                            <span style={{ fontWeight: "600" }}>Layout :</span>{" "}
                            <span>{data.siteDetails.layout}</span>
                        </div>
                        <div>
                            <span style={{ fontWeight: "600" }}>Mauza :</span>{" "}
                            <span>{data.siteDetails.mauza}</span>
                        </div>

                        <div>
                            <span style={{ fontWeight: "600" }}>Khasara No :</span>{" "}
                            <span>{data.siteDetails.khasaraNo}</span>
                        </div>
                        <div>
                            <span style={{ fontWeight: "600" }}>Regularized Area :</span>{" "}
                            <span>{data.siteDetails.regularizedArea ?? "--"}</span>
                        </div>

                        <div>
                            <span style={{ fontWeight: "600" }}>Plot area as per sale deed :</span>{" "}
                            <span>{data.siteDetails.plotAreaAsPerSaleDeed ?? "--"}</span>
                        </div>
                        {Number(data.siteDetails.builtUpArea || 0) > 0 && (
                            <div>
                                <span style={{ fontWeight: "600" }}>Built Up Area Regularised:</span>{" "}
                                <span>{data.siteDetails.builtUpArea ?? "--"}</span>
                            </div>
                        )}

                        <div>
                            <span style={{ fontWeight: "600" }}>Plot Area Surrendered :</span>{" "}
                            <span>{data.siteDetails.plotAreaSurrendered ?? "--"}</span>
                        </div>
                        {Number(data.siteDetails.areaToBeDemolished || 0) > 0 && (
                            <div>
                                <span
                                    style={{ fontWeight: "600" }}>Area of Construction To Be Demolished :</span>{" "}
                                <span>{data.siteDetails.areaToBeDemolished ?? "--"}</span>
                            </div>
                        )}
                    </div>

                </div>

                {/* BOX 3: Proposal Timeline Details */}
                <div className="qr-box-spacing-large">

                    <div className="qr-box-header-flat">
                        Proposal Timeline Details
                    </div>

                    <div className="qr-timeline-wrapper">
                        <DataTable
                            columns={timelineColumns}
                            data={timeline}
                            //customStyles={qrTableStyles}
                            customStyles={tableCustomStyles}
                            dense
                            noHeader
                            pagination={false}
                        />
                    </div>

                </div>

                {/* Close Button */}
                <div className="qr-close-container">
                    <button
                        className="qr-close-button"
                        onClick={() => window.close()}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    </>);
};

export default QRFiles;
